import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { catchError, map, Observable, throwError } from 'rxjs';
import { OrganizationRiderDetail, PaymentAccount, Rider, RiderCreationRequest, Trip } from '../../models';
import { DateUtil, isNullOrUndefined, RideRequestTypeConstants } from '@uzurv/core';
import { RideStatusConstants } from '../../constants';

@Injectable({
  providedIn: 'root'
})
export class RiderManagementService extends UzurvRestService {
  readonly resourceUrl = 'riderManagement/riders/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Create payment account for rider
   * @param riderId
   * @param paymentAccount
   * @permission rider_update
   */
  createPaymentAccount(riderId: number, paymentAccount: PaymentAccount): Observable<PaymentAccount> {
    return this.post<PaymentAccount>(`${this.resourceUrl}${riderId}/paymentAccounts/`, Serialize(paymentAccount))
      .pipe(
        map(response => {
          let savedPaymentAccount: PaymentAccount;

          if (response) {
            savedPaymentAccount = Deserialize(response, PaymentAccount);
            savedPaymentAccount = this.copyBillingInformation(paymentAccount, savedPaymentAccount);
          }
          return Deserialize(savedPaymentAccount, PaymentAccount);
        })
      )
      .pipe(
        catchError(err => {
          const message = isNullOrUndefined(err.message) ? '' : err.message;

          // Map to translated errors
          if (message.includes('Preauth failed')) {
            err.message = 'Error: Preauth failed';
          }

          return throwError(err);
        })
      );
  }

  /**
   * Creates rider with organization riders and service levels
   *
   * @param riderCreationRequest
   * @permission organization_rider_create
   */
  createRiderWithProfilesAndServiceLevels(riderCreationRequest: RiderCreationRequest): Observable<Rider> {
    return this.post<Rider>(`${this.resourceUrl}`, Serialize(riderCreationRequest)).pipe(map(response => Deserialize(response, Rider)));
  }

  /**
   * Update Rider
   *
   * @param riderId
   * @permission rider_update
   */
  updateRider(riderId: number, rider: Rider): Observable<Rider> {
    return this.put<Rider>(`${this.resourceUrl}${riderId}`, rider).pipe(map(response => Deserialize(response, Rider)));
  }

  /**
   * List Organization Riders
   *
   * @param riderId
   * @permission organization_rider_read
   */
  listOrganizationRiders(riderId: number): Observable<Array<OrganizationRiderDetail>> {
    return this.get<OrganizationRiderDetail[]>(`${this.resourceUrl}${riderId}/organizationRiders/`).pipe(
      map(response => Deserialize(response, OrganizationRiderDetail))
    );
  }

  /**
   * Returns a list of trips for a specific rider id.
   *
   * @param riderId
   * @param pageNumber
   * @param pageSize
   * @param date
   * @permission rider_management
   */
  listTripsByRiderId(riderId: number, pageNumber: number, pageSize: number, date: Date = null): Observable<Array<Trip>> {
    return this.get<Array<Trip>>(`${this.resourceUrl}${riderId}/trips/`, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      date: DateUtil.serializeLocalDate(date)
    }).pipe(map(response => Deserialize(response, Trip)));
  }

  /**
   * Copies values from one payment account to another
   *
   * @param from
   * @param to
   */
  private copyBillingInformation(from: PaymentAccount, to: PaymentAccount): PaymentAccount {
    if (from.billingStreetAddress1) {
      to.billingStreetAddress1 = from.billingStreetAddress1;
    }

    if (from.billingStreetAddress2) {
      to.billingStreetAddress2 = from.billingStreetAddress2;
    }

    if (from.billingState) {
      to.billingState = from.billingState;
    }

    if (from.billingCity) {
      to.billingCity = from.billingCity;
    }

    if (from.billingZipCode) {
      to.billingZipCode = from.billingZipCode;
    }

    return to;
  }
}
